import React, { useState } from "react";
import { Card, CardContent } from "@mui/material";
import MapIcon from '@mui/icons-material/Map';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import NavigationIcon from '@mui/icons-material/Navigation';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import Button from '@mui/material/Button';
import { useLatestRef } from '../../../utils/hooks';

export default function CameraView({ robot }) {
  const [miniMapPosition, setMiniMapPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [miniMapExpanded, setMiniMapExpanded] = useState(false);
  const [miniMapMinimized, setMiniMapMinimized] = useState(false);
  const [expandedCamera, setExpandedCamera] = useState(null);
  const [hasDragged, setHasDragged] = useState(false);

  const cameras = [
    { name: "Front Camera", position: "front" },
    { name: "Back Camera", position: "back" },
    { name: "Left Camera", position: "left" },
    { name: "Right Camera", position: "right" }
  ];

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setHasDragged(false);
    setDragStart({
      x: e.clientX - miniMapPosition.x,
      y: e.clientY - miniMapPosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setHasDragged(true);
      setMiniMapPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMoveRef = useLatestRef(handleMouseMove);
  const handleMouseUpRef = useLatestRef(handleMouseUp);

  const handleBubbleClick = () => {
    if (!hasDragged) {
      setMiniMapMinimized(false);
    }
  };

  React.useEffect(() => {
    if (isDragging) {
      const onMouseMove = (event) => handleMouseMoveRef.current(event);
      const onMouseUp = () => handleMouseUpRef.current();
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      return () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    }
  }, [isDragging, handleMouseMoveRef, handleMouseUpRef]);

  const toggleExpandCamera = (cameraIdx) => {
    if (expandedCamera === cameraIdx) {
      setExpandedCamera(null);
    } else {
      setExpandedCamera(cameraIdx);
    }
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative', background: '#0f172a', padding: 8 }}>
      {/* Expanded Single Camera */}
      {expandedCamera !== null ? (
        <Card sx={{ height: '100%', background: '#1e293b', border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#334155', padding: '8px 16px', borderBottom: '1px solid #475569', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, background: '#ef4444', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite alternate' }} />
                <span style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>{cameras[expandedCamera].name}</span>
              </div>
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedCamera(null)}
                sx={{ color: '#94a3b8', minWidth: 0, width: 32, height: 32 }}
              >
                <MinimizeIcon fontSize="small" />
              </Button>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
              <span style={{ color: '#64748b' }}>Live camera feed - {cameras[expandedCamera].position}</span>
            </div>
          </div>
        </Card>
      ) : (
        // 4 Camera Grid
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 8, height: '100%' }}>
          {cameras.map((camera, idx) => (
            <Card key={idx} sx={{ background: '#1e293b', border: '1px solid #334155', overflow: 'hidden', position: 'relative' }}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#334155', padding: '8px 16px', borderBottom: '1px solid #475569', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, background: '#ef4444', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite alternate' }} />
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>{camera.name}</span>
                  </div>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => toggleExpandCamera(idx)}
                    sx={{ color: '#22d3ee', minWidth: 0, width: 24, height: 24, opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    <CropSquareIcon fontSize="small" />
                  </Button>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
                  <span style={{ color: '#64748b' }}>Live camera feed - {camera.position}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Expanded Mini Map */}
      {miniMapExpanded && (
        <div style={{ position: 'absolute', inset: 16, zIndex: 30, background: 'rgba(15,23,42,0.95)', borderRadius: 12, border: '1px solid #334155' }}>
          <Card sx={{ height: '100%', background: 'rgba(30,41,59,0.95)', border: '1px solid #334155' }}>
            <div
              style={{ background: '#334155', padding: '12px 16px', borderBottom: '1px solid #475569', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseDown={handleMouseDown}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapIcon sx={{ color: '#22d3ee', fontSize: 20 }} />
                <span style={{ fontSize: 14, fontWeight: 500, color: 'white' }}>Live Map - Expanded</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <OpenWithIcon sx={{ color: '#64748b', fontSize: 18 }} />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setMiniMapExpanded(false)}
                  sx={{ color: '#94a3b8', minWidth: 0, width: 32, height: 32 }}
                >
                  <MinimizeIcon fontSize="small" />
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setMiniMapExpanded(false);
                    setMiniMapMinimized(true);
                  }}
                  sx={{ color: '#ef4444', minWidth: 0, width: 32, height: 32 }}
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </div>
            </div>
            <CardContent sx={{ p: 3, height: 'calc(100% - 57px)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, background: '#0f172a', borderRadius: 8, border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <NavigationIcon sx={{ color: '#22d3ee', fontSize: 48, mb: 1 }} />
                  <div style={{ color: '#94a3b8', fontSize: 18, marginBottom: 4 }}>{robot.location?.zone || "Unknown"}</div>
                  <div style={{ color: '#64748b', fontSize: 14 }}>
                    Position: X:{robot.location?.x || 0}, Y:{robot.location?.y || 0}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Normal Draggable Mini Map */}
      {!miniMapExpanded && !miniMapMinimized && (
        <div
          style={{
            position: 'absolute', zIndex: 20,
            left: `${miniMapPosition.x}px`,
            top: `${miniMapPosition.y}px`,
            width: '320px',
            height: '256px',
            cursor: isDragging ? 'grabbing' : 'default'
          }}
        >
          <Card sx={{ background: 'rgba(30,41,59,0.95)', border: '1px solid #334155', boxShadow: 8, overflow: 'hidden', height: '100%' }}>
            <div
              style={{ background: '#334155', padding: '8px 12px', borderBottom: '1px solid #475569', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseDown={handleMouseDown}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapIcon sx={{ color: '#22d3ee', fontSize: 18 }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: 'white' }}>Live Map</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <OpenWithIcon sx={{ color: '#64748b', fontSize: 16 }} />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setMiniMapExpanded(true)}
                  sx={{ color: '#22d3ee', minWidth: 0, width: 24, height: 24 }}
                >
                  <CropSquareIcon fontSize="small" />
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setMiniMapMinimized(true)}
                  sx={{ color: '#e0e0e0', minWidth: 0, width: 24, height: 24 }}
                >
                  <MinimizeIcon fontSize="small" />
                </Button>
              </div>
            </div>
            <CardContent sx={{ p: 2, height: 'calc(100% - 42px)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, background: '#0f172a', borderRadius: 8, border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <NavigationIcon sx={{ color: '#22d3ee', fontSize: 32, mb: 1 }} />
                  <div style={{ color: '#64748b', fontSize: 12 }}>{robot.location?.zone || "Unknown"}</div>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
                Position: X:{robot.location?.x || 0}, Y:{robot.location?.y || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Minimized Mini Map Bubble */}
      {miniMapMinimized && !miniMapExpanded && (
        <div
          style={{
            position: 'absolute', zIndex: 20,
            left: `${miniMapPosition.x}px`,
            top: `${miniMapPosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
        >
          <div
            style={{
              width: 64, height: 64,
              background: 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)',
              borderRadius: '50%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'grab', transition: 'transform 0.2s',
            }}
            onMouseDown={handleMouseDown}
            onClick={handleBubbleClick}
          >
            <MapIcon sx={{ color: 'white', fontSize: 32, animation: 'pulse 1s infinite alternate' }} />
          </div>
        </div>
      )}
    </div>
  );
}